import { Injectable } from '@angular/core';
import { EMPTY_ARRAY_LENGTH, NumberUtils } from '@modeso/aumico-lib-core-fe';
import {
    IEquity,
    IEquityEntry,
    IFinancialLineItemComponent,
    IFli,
    IFreeTable,
    IFreeTableEntry,
    IMovementScheduleComponent,
    IMovementScheduleEntries,
    IMovementScheduleFli,
    INotesInputComponent,
    INotesSelectComponent,
    ISuperTableCalculation,
    ISuperTableComponent,
    ISuperTableRow,
    SuperTableRowType
} from '@modeso/types__aumico-lib-annualreport-be';
import { IDynamicKeyValue } from '@modeso/types__aumico-lib-core-be';
import {
    IRichText,
    ISuperTableSegment,
    IXAxisConfigColumn,
    SuperTableColumnType,
} from '@modeso/types__aumico-lib-notes-be';
import Debug from 'debug';
import { cloneDeep, intersectionBy } from 'lodash-es';
import { MovementScheduleColumnIndexes } from '../enums/movementScheduleColumnIndexes.enum';
import { IFLIForNotes } from '../interfaces/IFLIForNotes.interface';
import { IFliTotalRow } from '../interfaces/IFliTotalRow.interface';
import { MappingItemsSelectionNode } from '../interfaces/mapping-items-selection-node.interface';
import { SuperTableMapper } from '../mappers/super-table.mapper';
import { MovementScheduleHelper } from './movementSchedule.helper';
import { findFlisById } from './notesCopy.helper';

const debug = Debug('aumico:aumico-lib-notes-fe:ComponentsAutoFillHelper');
@Injectable({ providedIn: 'root' })
export class ComponentsAutoFillHelper {
    constructor(
        private movementScheduleHelper: MovementScheduleHelper,
        private readonly superTableMapper: SuperTableMapper,
    ) { }

    autofillRichText(oldData: IRichText[]): IRichText[] {
        return oldData;
    }

    autofillEquity(oldData: IEquity): IEquity {
        const autoFilledData: IEquity = cloneDeep<IEquity>(oldData);
        let totalPreviousValue1 = 0;
        let totalPreviousValue2 = 0;
        const rowsToBeAutofill: IEquityEntry[] = [];
        // autofill row by get current date of the last financial state and set it as previous value for the new one
        for (const row of oldData.rows) {
            let previousValue1: number | null = null;
            if (row.currentValue1 != null && NumberUtils.isFinite(row.currentValue1)) {
                previousValue1 = NumberUtils.roundNumber(row.currentValue1);
                totalPreviousValue1 = NumberUtils.sumNumbers([totalPreviousValue1, previousValue1]);
            }

            let previousValue2: number | null = null;
            if (row.currentValue2 != null && NumberUtils.isFinite(row.currentValue2)) {
                previousValue2 = NumberUtils.roundNumber(row.currentValue2);
                totalPreviousValue2 = NumberUtils.sumNumbers([totalPreviousValue2, previousValue2]);
            }
            const rowToBeAutofill: IEquityEntry = {
                text: row.text,
                currentValue1: null,
                currentValue2: null,
                previousValue1,
                previousValue2,
            };

            rowsToBeAutofill.push(rowToBeAutofill);
        }
        autoFilledData.rows = rowsToBeAutofill;

        if (oldData.totalRow != null) {
            // autofill of total row
            autoFilledData.totalRow = {
                text: oldData.totalRow.text,
                currentValue1: 0,
                currentValue2: 0,
                previousValue1: totalPreviousValue1,
                previousValue2: totalPreviousValue2,
            };
        }

        return autoFilledData;
    }

    autofillFinancialLineItem(oldData: IFinancialLineItemComponent, flis: IFli[]): IFinancialLineItemComponent {
        const autoFilledData = cloneDeep<IFinancialLineItemComponent>(oldData);

        const oldSelectedFlis = [...oldData.flis] as IFli[];
        // order of intersection parameters is matter
        let intersectionOfFlisBetweenOldAndNew = intersectionBy<IFli, IFli>(flis, oldSelectedFlis, 'id');
        intersectionOfFlisBetweenOldAndNew = this.calculateFlisTotal(
            intersectionOfFlisBetweenOldAndNew,
            new Map(oldSelectedFlis.map((fli) => [fli.id, fli])),
        );
        const orderedAutoFilledFlis = this.orderNewFlisByOldFlis(intersectionOfFlisBetweenOldAndNew, oldSelectedFlis);

        const autofilledFlisWithNumbering = this.addNumberingToFlis(orderedAutoFilledFlis, oldSelectedFlis);

        autoFilledData.flis = [...autofilledFlisWithNumbering];

        return autoFilledData;
    }

    autoFillMovementSchedule(
        oldData: IMovementScheduleComponent,
        flis: IMovementScheduleFli[],
    ): IMovementScheduleComponent {
        const autoFilledData = cloneDeep<IMovementScheduleComponent>(oldData);

        const oldSelectedFlis = [...oldData.flis];

        // order of intersection parameters is matter
        let intersectionOfFlisBetweenOldAndNew = intersectionBy<IMovementScheduleFli, IMovementScheduleFli>(
            flis,
            oldSelectedFlis,
            'id',
        );

        intersectionOfFlisBetweenOldAndNew = this.movementScheduleHelper.addHeadersToMovementScheduleFlis(
            autoFilledData.headerOptions,
            intersectionOfFlisBetweenOldAndNew,
        );

        intersectionOfFlisBetweenOldAndNew = this.calculateMovementScheduleFlisTotal(
            intersectionOfFlisBetweenOldAndNew,
            new Map(oldSelectedFlis.map((fli) => [fli.id, fli])),
        );
        const orderedAutoFilledFlis = this.orderNewMovementScheduleFlisByOldFlis(
            intersectionOfFlisBetweenOldAndNew,
            oldSelectedFlis,
        );

        const autofilledFlisWithNumbering = this.addNumberingToFlis(orderedAutoFilledFlis, oldSelectedFlis);

        autoFilledData.flis = [...autofilledFlisWithNumbering];
        this.roundPreviousMovementsScheduleFlisValues(oldData.flis);
        autoFilledData.previousMovementScheduleComponent = { ...oldData, previousMovementScheduleComponent: null };

        return autoFilledData;
    }

    private roundPreviousMovementsScheduleFlisValues(movementSchedulesFlis: IMovementScheduleFli[]): void {
        for (const fli of movementSchedulesFlis) {
            for (const account of fli.accounts) {
                let column1: number | null = null;
                if (account.column1 != null && NumberUtils.isFinite(account.column1)) {
                    column1 = NumberUtils.roundNumber(account.column1);
                }

                let column2: number | null = null;
                if (account.column2 != null && NumberUtils.isFinite(account.column2)) {
                    column2 = NumberUtils.roundNumber(account.column2);
                }

                let column3: number | null = null;
                if (account.column3 != null && NumberUtils.isFinite(account.column3)) {
                    column3 = NumberUtils.roundNumber(account.column3);
                }

                let column4: number | null = null;
                if (account.column4 != null && NumberUtils.isFinite(account.column4)) {
                    column4 = NumberUtils.roundNumber(account.column4);
                }
                account.closeValue = NumberUtils.roundNumber(account.closeValue);
                account.column1 = column1;
                account.column2 = column2;
                account.column3 = column3;
                account.column4 = column4;
                account.openValue = NumberUtils.roundNumber(account.openValue);
            }
            fli.totalRow = this.movementScheduleHelper.calculateTotalOfMovementScheduleFli(fli);
        }
    }

    autoFillSuperTable(
        oldData: ISuperTableComponent,
        allMappingItemsSelectionTree: MappingItemsSelectionNode,
        flis: IFLIForNotes[]
    ): ISuperTableComponent {
        const autofillColumns = cloneDeep<IXAxisConfigColumn[]>(oldData.options.xAxisConfig);

        const autofillCalculations = cloneDeep<ISuperTableCalculation[]>(oldData.calculations);

        const autofillSegments = cloneDeep<ISuperTableSegment[]>(oldData.options.segments);

        const autofillRows = this.getSuperTableAutofillRows(
            oldData.rows,
            autofillColumns,
            allMappingItemsSelectionTree,
        );

        let shouldShowPreviousYear = oldData.options.showPreviousYear;
        if (shouldShowPreviousYear === undefined) {
            shouldShowPreviousYear = false;
        }
        const autofilledSuperTableData: ISuperTableComponent = {
            rows: autofillRows,
            referenceFlis: findFlisById(oldData.referenceFlis || [], flis),
            options: {
                title: oldData.options.title,
                xAxisConfig: autofillColumns,
                segments: autofillSegments,
                compareYears: oldData.options.compareYears,
                hideCurrentYear: oldData.options.hideCurrentYear,
                showFiscalYear: oldData.options.showFiscalYear,
                showCurrency: oldData.options.showCurrency,
                transpose: oldData.options.transpose,
                showPreviousYear: shouldShowPreviousYear,
                fxHideCurrentYear: oldData.options.fxHideCurrentYear,
                fxHidePreviousYear: oldData.options.fxHidePreviousYear,
            },
            calculations: autofillCalculations,
            addNumbering: oldData.addNumbering,
            footnotes: oldData.footnotes,
        };

        return autofilledSuperTableData;
    }



    private getSuperTableAutofillRows(
        oldRows: ISuperTableRow[],
        autofillColumns: IXAxisConfigColumn[],
        allMappingItemsSelectionTree: MappingItemsSelectionNode,
    ): ISuperTableRow[] {
        const autofilledRows: ISuperTableRow[] = [];
        oldRows.forEach((oldRow) => {
            let autofilledRow: ISuperTableRow = {
                rowId: oldRow.rowId,
                title: oldRow.title,
                type: oldRow.type,
                itemsIds: oldRow.itemsIds,
                // FX (REV-21512): rate rows never carry values into the new year (G1).
                previousValue: oldRow.type === SuperTableRowType.RATE ? undefined : oldRow.previousValue,
                currentValue:
                    oldRow.type === SuperTableRowType.FREE_TEXT || oldRow.type === SuperTableRowType.RATE
                        ? null
                        : oldRow.currentValue,
                financialtype: oldRow.financialtype,
                format: oldRow.format,
                rateSource: oldRow.rateSource,
            };

            if (oldRow.type === SuperTableRowType.TITLE) {
                autofilledRow.currentValue = oldRow.currentValue;
            }

            if (oldRow.itemsIds != null) {
                autofilledRow.itemsIds = oldRow.itemsIds.map((itemId) => {
                    const matchingNode = this.superTableMapper.findAutofilledMatchingNodeForRowItem(
                        itemId,
                        oldRow.type,
                        allMappingItemsSelectionTree,
                    );
                    return matchingNode ? this.superTableMapper.getNodeIdentifier(matchingNode) : itemId;
                });
            }

            if (oldRow.reconcileItems != null) {
                autofilledRow.reconcileItems = oldRow.reconcileItems.map((reconcileItem) => {
                    const matchingNode = this.superTableMapper.findAutofilledMatchingNodeForReconcileItem(
                        reconcileItem,
                        allMappingItemsSelectionTree,
                    );

                    if (matchingNode != null) {
                        return {
                            itemName: this.superTableMapper.getNodeIdentifier(matchingNode),
                            type: matchingNode.type,
                            financialtype: matchingNode.financialType,
                        };
                    }
                    return reconcileItem;
                });
            }

            for (const column of autofillColumns) {
                // FX (REV-21512): rate rows carry no values — the rate re-resolves in the new
                // report against its own rates and mapping (G1); no year shift either.
                if (oldRow.type === SuperTableRowType.RATE) {
                    continue;
                }
                if (column.type === SuperTableColumnType.PREVIOUS_YEAR && oldRow.type !== SuperTableRowType.TITLE) {
                    autofilledRow.previousValue = oldRow.currentValue;
                    autofilledRow.currentValue =
                        autofilledRow.currentValue || autofilledRow.currentValue === 0
                            ? autofilledRow.currentValue
                            : null;
                    continue;
                }

                if (
                    column.type === SuperTableColumnType.CURRENT_YEAR &&
                    autofilledRow.itemsIds.length !== EMPTY_ARRAY_LENGTH &&
                    oldRow.type !== SuperTableRowType.TITLE
                ) {
                    autofilledRow.currentValue = this.getCurrentYearValueAccodringToItemsIds(
                        autofilledRow.itemsIds,
                        allMappingItemsSelectionTree,
                        autofilledRow.type,
                        oldRow.currentValue,
                    );
                }

                if (column.type === SuperTableColumnType.CALCULATION && oldRow.type !== SuperTableRowType.TITLE) {
                    // no need to copy calculation values since they will be recalculated in the super table form
                    autofilledRow[column.columnId] = 0;
                    continue;
                }


                autofilledRow[column.columnId] = oldRow[column.columnId];

            }
            autofilledRows.push(autofilledRow);
        });
        return autofilledRows;
    }

    private getCurrentYearValueAccodringToItemsIds(
        itemsIds: string[],
        allMappingItemsSelectionTree: MappingItemsSelectionNode,
        type: SuperTableRowType,
        previousValue: number = 0,
    ): number {
        const matchingNodes: MappingItemsSelectionNode[] = [];
        let currentYearValues: number[] = [];

        for (const itemId of itemsIds) {
            const matchingNodesForId = this.superTableMapper.findAllMatchingNodesForRowItem(
                itemId,
                type,
                allMappingItemsSelectionTree,
            );
            if (matchingNodesForId.length === 1) {
                const matchingNode = matchingNodesForId[0];
                currentYearValues.push(matchingNode.data.calculatedClosingBalance);
                matchingNodes.push(matchingNode);
            }
            if (matchingNodesForId.length > 1) {
                // Need to identify which node to take, we do that based on the previous value
                // if that still casuses an issue, then we accept that mistake.
                for (const matchingNode of matchingNodesForId) {
                    if (matchingNode.data.calculatedOpeningBalance === previousValue) {
                        currentYearValues.push(matchingNode.data.calculatedClosingBalance);
                        matchingNodes.push(matchingNode);
                        break;
                    }
                }
            }
        }

        return NumberUtils.sumIgnoringInfiniteNumbers(currentYearValues, 0);
    }

    autofillFreeTable(oldData: IFreeTable, flis: IFLIForNotes[]): IFreeTable {
        const autoFilledData = cloneDeep<IFreeTable>({
            ...oldData,
            referenceFlis: findFlisById(oldData.referenceFlis || [], flis),
        });
        let totalPreviousValue = 0;
        const newRowsToBeAutofill: IFreeTableEntry[] = [];
        for (const row of oldData.rows) {
            let previousValue: number | null = null;
            if (row.currentValue != null && NumberUtils.isFinite(row.currentValue)) {
                previousValue = NumberUtils.roundNumber(row.currentValue);
                totalPreviousValue = NumberUtils.sumNumbers([totalPreviousValue, previousValue]);
            }
            const rowToBeAutofill: IFreeTableEntry = {
                text: row.text,
                currentValue: null,
                previousValue,
            };
            newRowsToBeAutofill.push(rowToBeAutofill);
        }

        autoFilledData.rows = newRowsToBeAutofill;
        if (oldData.totalRow != null) {
            // autofill of total row
            autoFilledData.totalRow = {
                text: oldData.totalRow.text,
                currentValue: 0,
                previousValue: totalPreviousValue,
            };
        }

        return autoFilledData;
    }

    autofillSelect(oldData: INotesSelectComponent): INotesSelectComponent {
        const autoFilledData = cloneDeep<INotesSelectComponent>(oldData);

        const oldSelectedCurrentValue = oldData.currentYearValue as string;
        const isOldCurrentValueHasNoSelection = oldSelectedCurrentValue == null || oldSelectedCurrentValue == '';
        if (!isOldCurrentValueHasNoSelection) {
            autoFilledData.previousYearValue = this.getOldCurrentSelectionFromNewSelectionOptions(
                oldSelectedCurrentValue,
                oldData.options.selectionOption,
            );
        }

        autoFilledData.currentYearValue = null;

        return autoFilledData;
    }

    autofillInput(oldData: INotesInputComponent): INotesInputComponent {
        const autoFilledData: INotesInputComponent = cloneDeep<INotesInputComponent>(oldData);

        if (!oldData.options.compareYears) {
            autoFilledData.currentYearValue = oldData.currentYearValue;
            return autoFilledData;
        }

        autoFilledData.previousYearValue = oldData.currentYearValue;
        autoFilledData.currentYearValue = '';

        return autoFilledData;
    }

    private getOldCurrentSelectionFromNewSelectionOptions(
        oldSelectedCurrentValue: string,
        newSelectionOptions: IDynamicKeyValue[],
    ): string {
        for (const option of newSelectionOptions) {
            const languageAsKeysOfSelections = Object.keys(option);

            for (const language of languageAsKeysOfSelections) {
                if (option[language] == oldSelectedCurrentValue) {
                    return option[language];
                }
            }
        }

        return '';
    }

    private orderNewFlisByOldFlis(newFlis: IFli[], oldFlis: IFli[]): IFli[] {
        const orderedNewFlis = newFlis.sort((object1, object2) => {
            return oldFlis.findIndex((fli) => fli.id == object1.id) - oldFlis.findIndex((fli) => fli.id == object2.id);
        });

        return orderedNewFlis;
    }

    private orderNewMovementScheduleFlisByOldFlis(
        newFlis: IMovementScheduleFli[],
        oldFlis: IMovementScheduleFli[],
    ): IMovementScheduleFli[] {
        const orderedNewFlis = newFlis.sort((object1, object2) => {
            return oldFlis.findIndex((fli) => fli.id == object1.id) - oldFlis.findIndex((fli) => fli.id == object2.id);
        });

        return orderedNewFlis;
    }

    private calculateMovementScheduleFlisTotal(
        newMovementScheduleFlis: IMovementScheduleFli[],
        oldMovementScheduleFlis: Map<string, IMovementScheduleFli>,
    ): IMovementScheduleFli[] {
        const flisWithCalculatedTotal: IMovementScheduleFli[] = [];

        for (const fli of newMovementScheduleFlis) {
            const totalRowOfCurrentFli: IMovementScheduleEntries = {
                closeValue: this.movementScheduleHelper.getTotalByColumn(
                    MovementScheduleColumnIndexes.CLOSE_VALUE,
                    fli.accounts,
                ),
                column1: 0,
                column2: 0,
                column3: 0,
                column4: 0,
                openValue: this.movementScheduleHelper.getTotalByColumn(
                    MovementScheduleColumnIndexes.OPEN_VALUE,
                    fli.accounts,
                ),
                text: oldMovementScheduleFlis.get(fli.id).totalRow.text,
            };
            const currentFliWithTotal = { ...fli, totalRow: totalRowOfCurrentFli };
            flisWithCalculatedTotal.push(currentFliWithTotal);
        }

        return flisWithCalculatedTotal;
    }

    private calculateFlisTotal(newFlis: IFli[], oldFlis: Map<string, IFli>): IFli[] {
        const flisWithCalculatedTotal: IFli[] = [];

        for (const fli of newFlis) {
            let totalCurrentValue = 0;
            let totalPreviousValue = 0;
            for (const account of fli.accounts) {
                if (account.accountName === '' && account.id.toString() === '') {
                    continue;
                }

                totalCurrentValue = NumberUtils.sumNumbers([totalCurrentValue, account.currentValue]);
                totalPreviousValue = NumberUtils.sumNumbers([totalPreviousValue, account.previousValue]);
            }

            const totalRowOfCurrentFli: IFliTotalRow = {
                accountName: oldFlis.get(fli.id).totalRow.accountName,
                currentValue: totalCurrentValue,
                previousValue: totalPreviousValue,
            };
            const currentFliWithTotal = { ...fli, totalRow: totalRowOfCurrentFli };
            flisWithCalculatedTotal.push(currentFliWithTotal);
        }

        return flisWithCalculatedTotal;
    }

    private addNumberingToFlis(
        newFlis: IMovementScheduleFli[] | IFli[],
        oldFlis: IMovementScheduleFli[] | IFli[],
    ): IMovementScheduleFli[] | IFli[] {
        const updatedNewFlis = newFlis.map((newFli) => {
            const matchingFli = (oldFlis as any[]).find((fli) => fli.id === newFli.id);
            return matchingFli ? { ...newFli, addNumbering: matchingFli.addNumbering } : newFli;
        });

        return updatedNewFlis;
    }
}
